import { supabase } from '../supabase';
import { uploadMarketCommentAttachment } from './market_comments';

export interface MarketPriceCapturePayload {
  exporter_id: number;
  shipment_id: number | null;
  container_number: string | null;
  exporter_ref: string | null;
  seller: string | null;
  ship_name: string | null;
  season: string | null;
  species_id: number | null;
  sale_date: string;
  market_id: string | null;
  currency: string;
  content: string | null;
  has_photos: boolean;
  items: {
    box_ids: number[];
    price: number | null;
    currency: string;
    sale_date: string;
  }[];
  products: {
    variety: string;
    caliber: string;
    weight: number;
    entries: {
      price: number;
      count: number;
      label: string | null;
      format: string | null;
      category: string | null;
    }[];
  }[];
  manual_entries: {
    variety: string;
    caliber: string;
    weight: number;
    price: number;
    format: string | null;
    color: string | null;
    label: string | null;
    category: string | null;
  }[];
}

/** One immutable draft is retried until its DB capture and photos finish. */
export interface MarketPriceCaptureIntent {
  requestId: string;
  userId: number;
  payload: MarketPriceCapturePayload;
  photos: File[];
  commentIds: number[] | null;
  uploaded: Set<string>;
  uncertainCommit: boolean;
}

export function createMarketPriceCaptureIntent(
  payload: MarketPriceCapturePayload,
  photos: File[],
  userId: number
): MarketPriceCaptureIntent {
  return {
    requestId: crypto.randomUUID(),
    userId,
    payload: JSON.parse(JSON.stringify(payload)),
    photos: [...photos],
    commentIds: null,
    uploaded: new Set(),
    uncertainCommit: false,
  };
}

export async function completeMarketPriceCapture(
  intent: MarketPriceCaptureIntent
): Promise<void> {
  if (intent.commentIds === null) {
    try {
      const { data, error } = await supabase.rpc('save_market_price_capture', {
        p_request: intent.requestId,
        p_payload: intent.payload,
      });
      if (error) throw error;
      if (
        !Array.isArray(data) ||
        data.some((id) => !Number.isSafeInteger(id) || id <= 0) ||
        new Set(data).size !== data.length
      )
        throw new Error('Invalid capture response');
      intent.commentIds = data;
    } catch (error) {
      // A SQL rejection rolls back the transaction. A transport failure may
      // have lost a committed response, so retain the same operation ID.
      const code = (error as { code?: string } | null)?.code;
      if (!code || !/^[0-9A-Z]{5}$/.test(code)) intent.uncertainCommit = true;
      throw error;
    }
  }
  for (const commentId of intent.commentIds)
    for (let index = 0; index < intent.photos.length; index++) {
      const key = `${commentId}:${index}`;
      if (intent.uploaded.has(key)) continue;
      await uploadMarketCommentAttachment({
        commentId,
        exporterId: intent.payload.exporter_id,
        created_by: intent.userId,
        file: intent.photos[index],
        requestKey: `${intent.requestId}-${index}`,
      });
      intent.uploaded.add(key);
    }
}
